import { VENUE_TABS } from "@/@core/constants/venue-tabs";
import QuestionMarkCircleIcon from "@heroicons/react/24/outline/QuestionMarkCircleIcon";
import ChatBubbleOvalLeftIcon from "@heroicons/react/24/solid/ChatBubbleOvalLeftIcon";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import WhatsappLinkWithBeacon from "@/components/WhatsappLinkWithBeacon";
import { WHATSAPP_SECTIONS } from "@/@core/enums/whatsapp_tags.enum";
import { useVenueDetail } from "@/hooks/venueContext";
import { formatPhoneNumber } from "@/lib/format-phone-number";
import { useTranslation } from "@/hooks/translationContext";

const DEFAULT_TITLE_KEY = "whatsapp-ask-question-title";
const DEFAULT_DESCRIPTION_KEY = "whatsapp-ask-question-description";
const INPUT_PLACEHOLDER_DESCRIPTION_KEY = "whatsapp-ask-question-input-placeholder";

export default function VenueDetailAskQuestion() {
  const [text, setText] = useState("");
  const { venue } = useVenueDetail();
  const { t } = useTranslation();

  const getWpHref = () => {
    const key = "whatsapp-ask-question-default-message";
    const defaultMessage = t(key, {
      venue: venue?.descriptions?.[0]?.title,
    });

    const fullText =
      defaultMessage !== key ? defaultMessage + " " + text : text;

    return `whatsapp://send?phone=${formatPhoneNumber(venue?.whatsappPhoneNumber)}&text=${encodeURIComponent(fullText)}`;
  };

  const title = t(DEFAULT_TITLE_KEY, {
    venue: venue?.descriptions?.[0]?.title,
    defaultValue: "Hemen Soru Sorun"
  });

  const description = t(DEFAULT_DESCRIPTION_KEY, {
    venue: venue?.descriptions?.[0]?.title,
  });

  const inputPlaceholder = t(INPUT_PLACEHOLDER_DESCRIPTION_KEY, {
    venue: venue?.descriptions?.[0]?.title,
  });

  return (
    <>
      <div
        id={VENUE_TABS.ASK_QUESTION}
        className="flex relative flex-col mt-6 mb-6 lg:max-w-[895px] border rounded-[32px] p-4 gap-4"
      >
        <div className="flex items-center bg-stone-50 rounded-[16px] py-1.5 px-2 gap-2">
          <QuestionMarkCircleIcon className="size-6 text-secondary" />
          <h2 className="text-secondary font-bold text-sm">{title}</h2>
        </div>

        {description && description !== DEFAULT_DESCRIPTION_KEY && (
          <div>
            <span className="text-assistant text-xs">{description}</span>
          </div>
        )}

        <TextArea
          placeholder={inputPlaceholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />

        <WhatsappLinkWithBeacon
          title={t("venue-detail-contact-button-title", {
            venue: venue?.descriptions?.[0]?.title,
          })}
          section={WHATSAPP_SECTIONS.VENUE_DETAIL}
          disabled={!venue?.whatsappPhoneNumber}
          venueId={venue?.id}
          href={getWpHref()}
        >
          <Button
            variant="primary"
            type="button"
            className={
              "w-full px-4 py-3 text-center text-sm font-bold text-white rounded-2xl bg-gradient-to-r from-rose-600 to-pink-700"
            }
          >
            Gönder
          </Button>
        </WhatsappLinkWithBeacon>
      </div>
    </>
  );
}

function TextArea({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  placeholder: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}) {
  const { t } = useTranslation();
  const [shadow, setShadow] = useState(false);

  return (
    <div className="w-full rounded-3xl">
      <div
        className={cn(
          `w-full py-2 px-4 rounded-[24px] flex gap-2 border border-assistant-secondary bg-white duration-200`,
          shadow && "shadow-lg",
        )}
      >
        <div className="p-2 text-assistant">
          <ChatBubbleOvalLeftIcon className="size-5" />
        </div>

        <textarea
          value={value}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e)}
          id="user-question"
          placeholder={
            placeholder !== INPUT_PLACEHOLDER_DESCRIPTION_KEY
              ? placeholder
              : t("venue-detail-ask-question-placeholder")
          }
          onFocus={() => setShadow(true)}
          onBlur={() => setShadow(false)}
          className="py-2 w-full text-sm font-bold outline-none !h-44 resize-none text-black placeholder:text-assistant"
        />
      </div>
    </div>
  );
}
